/**
 * Декодер штрихкода с потока камеры (перенос `CameraScanner` старого клиента,
 * задача 366). В этом пакете — только родной `BarcodeDetector` (Android Chrome,
 * часть Safari): запасной ZXing старого клиента требует объявить
 * `@zxing/browser` зависимостью пакета, а это правка package-lock — запрос
 * интегратору. Без детектора экран честно предлагает ручной ввод и сканер-клавиатуру.
 */
export interface BarcodeStreamDecoder {
  start(video: HTMLVideoElement, onCode: (code: string) => void): void
  stop(): void
}

export type BarcodeDecoderFactory = () => BarcodeStreamDecoder | null

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code']
const POLL_MS = 250

interface DetectedBarcode {
  rawValue: string
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>
}
type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike

function nativeDetector(): BarcodeDetectorConstructor | null {
  const candidate = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector
  return typeof candidate === 'function' ? candidate : null
}

class NativeDecoder implements BarcodeStreamDecoder {
  private timer: ReturnType<typeof setInterval> | null = null
  private busy = false
  private readonly detector: BarcodeDetectorConstructor

  constructor(detector: BarcodeDetectorConstructor) {
    this.detector = detector
  }

  start(video: HTMLVideoElement, onCode: (code: string) => void): void {
    const detector = new this.detector({ formats: FORMATS })
    this.timer = setInterval(() => {
      if (this.busy || video.readyState < 2) return
      this.busy = true
      detector
        .detect(video)
        .then((found) => {
          const code = found[0]?.rawValue.trim()
          if (code) onCode(code)
        })
        .catch(() => {
          // Отказ на одном кадре — не отказ сканера.
        })
        .finally(() => {
          this.busy = false
        })
    }, POLL_MS)
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer)
    this.timer = null
  }
}

export const createNativeDecoder: BarcodeDecoderFactory = () => {
  const detector = nativeDetector()
  return detector === null ? null : new NativeDecoder(detector)
}

/** Есть ли в браузере всё для скана камерой: поток (HTTPS/localhost) и детектор. */
export function cameraScanAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    nativeDetector() !== null
  )
}
